import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import styles from "./Step3MemberPhoneVerification.module.css";
import parentStyles from "../MemberOnboarding.module.css";
import VerificationGraphic from "../icons/VerificationGraphic";
import SmsIcon from "../icons/SmsIcon";
import VoiceCallIcon from "../icons/VoiceCallIcon";
import SecurityShieldIcon from "../icons/SecurityShieldIcon";
import { updatePhoneV2API, sendOtpToPhoneV2API, verifyPhoneNumAPI, verifyPhoneV2API } from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import { setUser } from "../../../../redux/modules/userSlice";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

interface Step3Props {
  onNext: (increment?: number | any) => void;
  onBack: () => void;
  canGoBack?: boolean;
  initialStepState?: "SELECT_METHOD" | "ENTER_OTP";
  initialPhoneNumber?: string;
  phoneNumber?: string;
}

const phoneSchema = yup.object({
  phoneNumber: yup
    .string()
    .required("Phone number is required")
    .test("valid-phone", "Please enter a valid phone number", (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, "");
      if (digits.length < 8 || digits.length > 15) return false;
      const normalized = value.trim().startsWith("+") ? value.trim() : `+${value.trim()}`;
      return /^\+[1-9]\d{6,14}$/.test(normalized.replace(/[\s\-\(\)]/g, ""));
    }),
}).required();

export interface CountryData {
  name: string;
  dialCode: string;
  countryCode: string;
  format: string;
}

export const getExpectedDigits = (
  countryData: CountryData | null | undefined
): { min: number; max: number } => {
  if (countryData && countryData.format) {
    const dotCount = (countryData.format.match(/\./g) || []).length;
    const dialDigits = (countryData.dialCode || "").length;
    const nationalDigits = dotCount - dialDigits;
    if (nationalDigits > 0) {
      return { min: nationalDigits, max: nationalDigits };
    }
  }
  return { min: 7, max: 12 };
};

export const validatePhoneNumber = (
  value: string,
  countryData: CountryData | null | undefined
): string | null => {
  if (!countryData || !countryData.dialCode) {
    return "Country code is required. Please choose your country.";
  }

  const rawDigits = (value || "").replace(/\D/g, "");
  const dialDigits = (countryData.dialCode || "").replace(/\D/g, "");

  const nationalDigits = rawDigits.startsWith(dialDigits)
    ? rawDigits.slice(dialDigits.length)
    : rawDigits;

  if (!nationalDigits || nationalDigits.length === 0) {
    return "Phone number is required";
  }

  const { min, max } = getExpectedDigits(countryData);
  const countryName = countryData.name || "selected country";

  if (min === max) {
    if (nationalDigits.length < min) {
      return `Please enter all ${min} digits for ${countryName}`;
    }
    if (nationalDigits.length > max) {
      return `Phone number cannot exceed ${max} digits for ${countryName}`;
    }
  } else {
    if (nationalDigits.length < min || nationalDigits.length > max) {
      return `Phone number must be between ${min} and ${max} digits for ${countryName}`;
    }
  }

  return null;
};

const formatPhoneNumberForApi = (phone: string): string => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  return digits ? `+${digits}` : "";
};

const formatPhoneNumberForInput = (phone: string): string => {
  if (!phone) return "";
  const trimmed = phone.trim();
  if (trimmed.startsWith("+")) {
    return trimmed;
  }
  const digits = trimmed.replace(/\D/g, "");
  return digits ? `+${digits}` : "";
};

const otpSchema = yup.object({
  otp: yup
    .string()
    .length(6, "Please enter all 6 digits")
    .required("Verification code is required"),
}).required();

type PhoneFormData = yup.InferType<typeof phoneSchema>;
type OtpFormData = yup.InferType<typeof otpSchema>;

export default function Step3MemberPhoneVerification({
  onNext,
  onBack,
  canGoBack = true,
  initialStepState,
  initialPhoneNumber,
  phoneNumber,
}: Step3Props) {
  const dispatch = useDispatch();

  const getStoredPhoneNumber = (): string => {
    if (phoneNumber) return phoneNumber;
    if (initialPhoneNumber) return initialPhoneNumber;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        if (u.phoneNumber) return u.phoneNumber;
      }
    } catch { }
    return "";
  };

  const rawPhone = getStoredPhoneNumber();
  const initialPhoneFormatted = formatPhoneNumberForInput(rawPhone);

  const [phoneNumberDisplay, setPhoneNumberDisplay] = useState(
    rawPhone ? formatPhoneNumberForApi(rawPhone) : ""
  );

  const [stepState, setStepState] = useState<"SELECT_METHOD" | "ENTER_OTP">(() => {
    if (initialStepState) return initialStepState;
    return "SELECT_METHOD";
  });

  const [method, setMethod] = useState<"sms" | "call">("sms");
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(299);
  const [isResending, setIsResending] = useState(false);

  const [phoneRawValue, setPhoneRawValue] = useState<string>(() => {
    return initialPhoneFormatted ? initialPhoneFormatted.replace(/\D/g, "") : "";
  });
  const [selectedCountry, setSelectedCountry] = useState<CountryData | null>(null);
  const [phoneError, setPhoneError] = useState<string>("");

  const [otpArray, setOtpArray] = useState<string[]>(Array(6).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const phoneForm = useForm<PhoneFormData>({
    resolver: yupResolver(phoneSchema),
    defaultValues: { phoneNumber: initialPhoneFormatted || "" }
  });

  const otpForm = useForm<OtpFormData>({
    resolver: yupResolver(otpSchema),
  });

  useEffect(() => {
    if (initialPhoneFormatted) {
      setPhoneRawValue(initialPhoneFormatted.replace(/\D/g, ""));
      phoneForm.setValue("phoneNumber", initialPhoneFormatted, { shouldValidate: true });
      phoneForm.clearErrors("phoneNumber");
    }
  }, [initialPhoneFormatted, phoneForm]);

  useEffect(() => {
    phoneForm.register("phoneNumber");
    otpForm.register("otp");
  }, [phoneForm, otpForm]);

  useEffect(() => {
    if (stepState !== "ENTER_OTP" || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [stepState, timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [stepState]);

  const handlePhoneChange = (value: string, data: any) => {
    setPhoneRawValue(value);
    if (data && data.dialCode) {
      setSelectedCountry(data);
    }

    const fullPhone = value ? (value.startsWith("+") ? value : `+${value}`) : "";
    phoneForm.setValue("phoneNumber", fullPhone);

    if (phoneError) {
      const activeCountry = data && data.dialCode ? data : selectedCountry;
      const err = validatePhoneNumber(value, activeCountry);
      setPhoneError(err || "");
    }
  };

  const handlePhoneBlur = () => {
    const err = validatePhoneNumber(phoneRawValue, selectedCountry);
    setPhoneError(err || "");
  };

  const onPhoneSubmit = async () => {
    const validationErr = validatePhoneNumber(phoneRawValue, selectedCountry);
    if (validationErr) {
      setPhoneError(validationErr);
      return;
    }
    setPhoneError("");

    setIsUpdatingPhone(true);
    try {
      const formattedPhone = formatPhoneNumberForApi(phoneRawValue);

      // 1. Update phone number first (must succeed before OTP API)
      const updatePayload = {
        phoneNumber: formattedPhone,
      };

      const updateRes: any = await updatePhoneV2API(updatePayload);
      const updateData = updateRes?.data || updateRes;

      if (updateData?.success === false) {
        throw new Error(updateData?.message || "Failed to update phone number.");
      }

      if (updateData?.user) {
        localStorage.setItem("user", JSON.stringify(updateData.user));
        dispatch(setUser(updateData.user));
      }

      // 2. Send OTP to phone with preferred_channel ("sms" or "voice")
      const otpPayload = {
        preferred_channel: method === "sms" ? "sms" : "voice",
      };

      const otpRes: any = await sendOtpToPhoneV2API(otpPayload);
      const otpData = otpRes?.data || otpRes;

      setPhoneNumberDisplay(formattedPhone);
      showToast(otpData?.message || updateData?.message || `Verification code sent via ${method === "sms" ? "SMS" : "call"}!`);
      setTimeLeft(299);
      setStepState("ENTER_OTP");
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to send verification code. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  const handleSkip = async () => {
    const validationErr = validatePhoneNumber(phoneRawValue, selectedCountry);
    if (validationErr) {
      setPhoneError(validationErr);
      return;
    }
    setPhoneError("");

    setIsUpdatingPhone(true);
    try {
      const formattedPhone = formatPhoneNumberForApi(phoneRawValue);

      const payload = {
        phoneNumber: formattedPhone,
      };

      const res: any = await updatePhoneV2API(payload);
      const resData = res?.data || res;

      if (resData?.success || res?.status === 200 || res?.status === 201) {
        try {
          const stored = localStorage.getItem("user");
          const currentUser = stored ? JSON.parse(stored) : {};
          const updatedUser = {
            ...currentUser,
            ...(resData?.user || {}),
            phoneNumber: formattedPhone,
          };
          localStorage.setItem("user", JSON.stringify(updatedUser));
          dispatch(setUser(updatedUser));
          console.log("[Step3MemberPhoneVerification] Phone skipped/saved -> Updated user in localStorage/Redux:", updatedUser);
        } catch { }

        showToast(resData?.message || "Phone number saved!");
        console.log("[Step3MemberPhoneVerification] Calling onNext({ phoneNumber, phoneSkipped: true })");
        onNext({ phoneNumber: formattedPhone, phoneSkipped: true });
      } else {
        showToast(resData?.message || "Failed to update phone number. Please try again.", "error");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to update phone number. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  const onOtpSubmit = async (data: OtpFormData) => {
    setIsVerifying(true);
    try {
      const payload = {
        otp: String(data.otp).trim(),
      };

      const res: any = await verifyPhoneV2API(payload);
      const resData = res?.data || res;

      if (resData?.success || res?.status === 200 || res?.status === 201) {
        try {
          const stored = localStorage.getItem("user");
          const currentStoredUser = stored ? JSON.parse(stored) : {};
          const updatedUser = {
            ...currentStoredUser,
            ...(resData?.user || {}),
            phoneVerified: true,
          };
          localStorage.setItem("user", JSON.stringify(updatedUser));
          dispatch(setUser(updatedUser));
          console.log("[Step3MemberPhoneVerification] Phone OTP verified successfully -> Updated user in localStorage/Redux:", updatedUser);
        } catch { }
        showToast(resData?.message || "Phone number verified successfully!");
        console.log("[Step3MemberPhoneVerification] Calling onNext() -> proceeding to Step 4");
        onNext();
      } else {
        showToast(resData?.message || "Failed to verify phone number. Please try again.", "error");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to verify phone number. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (isResending || timeLeft > 0) return;

    setIsResending(true);
    try {
      const payload = {
        preferred_channel: method === "sms" ? "sms" : "voice",
      };

      const res: any = await sendOtpToPhoneV2API(payload);
      const resData = res?.data || res;

      if (resData?.success || res?.status === 200 || res?.status === 201) {
        showToast(resData?.message || "Verification code resent!");
        setTimeLeft(299);
      } else {
        showToast(resData?.message || "Failed to resend code. Please try again.", "error");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to resend code. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsResending(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otpArray];
    newOtp[index] = value;
    setOtpArray(newOtp);
    otpForm.setValue("otp", newOtp.join(""), { shouldValidate: true });

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleBack = () => {
    if (stepState === "ENTER_OTP") {
      setStepState("SELECT_METHOD");
    } else {
      if (!canGoBack) {
        showToast("Cannot go backward", "info");
        return;
      }
      onBack();
    }
  };

  if (stepState === "ENTER_OTP") {
    return (
      <div className={`${styles.formCard} ${styles.formCardSMS}`}>
        <h1 className={styles.formTitleSMS}>Verify your phone number</h1>
        <p className={styles.formSubtitleSMS}>
          SMS verification is required for all Member accounts to prevent fraud
          and protect your Account.
        </p>

        <div className={styles.graphicContainerSMS}>
          <VerificationGraphic />
        </div>

        <div className={styles.phoneNumberSMS}>{phoneNumberDisplay || "+1 0987654321"}</div>
        <div className={styles.otpInstructionSMS}>
          Code sent via {method === "sms" ? "text" : "call"} to
        </div>

        <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} noValidate>
          <div className={styles.otpContainerSMS}>
            {[...Array(6)].map((_, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                maxLength={1}
                value={otpArray[i]}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                className={styles.otpInputSMS}
                placeholder="0"
              />
            ))}
          </div>

          {otpForm.formState.errors.otp && (
            <p className={styles.errorTextSMS} style={{ textAlign: "center", marginTop: "-16px", marginBottom: "16px" }}>
              {otpForm.formState.errors.otp.message}
            </p>
          )}

          <div className={styles.resendTextSMS}>
            Resend Code :{" "}
            {timeLeft > 0 ? (
              <span className={styles.timerSMS}>After {formatTime(timeLeft)}</span>
            ) : (
              <button
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  color: "#FD3C84",
                  cursor: isResending ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  padding: 0,
                  textDecoration: "underline",
                  fontSize: "inherit",
                  fontFamily: "inherit",
                  opacity: isResending ? 0.7 : 1,
                }}
                onClick={handleResendOtp}
                disabled={isResending}
              >
                {isResending ? "Sending..." : "Resend now"}
              </button>
            )}
          </div>

          <div className={`${styles.actionRowSMS} ${styles.actionRowWithMarginSMS}`}>
            <button type="button" className={styles.backBtnSMS} onClick={handleBack} disabled={isVerifying}>
              <ArrowLeft size={18} /> Back
            </button>
            <button type="submit" className={styles.continueBtnSMS} disabled={isVerifying}>
              <span className={styles.desktopBtnTextSMS}>
                {isVerifying ? "Verifying..." : "Continue"}
              </span>
              <span className={styles.mobileBtnTextSMS}>
                {isVerifying ? "Verifying..." : "Verify Phone Number"}
              </span>
            </button>
          </div>
        </form>

        <div className={styles.footerLinkSMS}>
          <button className={styles.pinkLinkSMS} onClick={() => setStepState("SELECT_METHOD")}>
            Change number or method
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formCard}>
      <h1 className={styles.formTitle}>Verify your phone number</h1>
      <p className={styles.formSubtitle}>
        SMS verification is required for all Member accounts to prevent fraud
        and protect your Account.
      </p>

      <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} noValidate>
        <div className={styles.inputGroup}>
          <label htmlFor="mobile-number-input" className={styles.label}>Mobile number</label>
          <div className={`${styles.phoneInputWrapper} ${phoneError ? styles.inputError : ""}`}>
            <PhoneInput
              country={selectedCountry?.countryCode || ""}
              value={phoneRawValue}
              onChange={(value, data: any) => {
                handlePhoneChange(value, data);
              }}
              onBlur={handlePhoneBlur}
              onMount={(value: string, data: any) => {
                if (data && (data as any).dialCode) {
                  setSelectedCountry(data as CountryData);
                }
              }}
              inputProps={{
                id: "mobile-number-input",
                name: "phoneNumber",
                required: true,
                disabled: isUpdatingPhone,
              }}
              enableSearch={true}
              searchPlaceholder="Search country or code..."
              searchNotFound="No country found"
              preferredCountries={["us", "gb", "ca", "au", "in"]}
              countryCodeEditable={false}
              disableCountryCode={false}
              specialLabel=""
              autoFormat={true}
              placeholder={selectedCountry?.format ? undefined : ""}
              containerClass={styles.phoneInputContainer}
              inputClass={styles.phoneInput}
              buttonClass={styles.phoneFlagButton}
              dropdownClass={styles.phoneDropdown}
              searchClass={styles.phoneSearch}
            />
          </div>
          {phoneError && (
            <p className={parentStyles.errorText}>{phoneError}</p>
          )}
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>Verification method</label>
          <div className={styles.methodCards}>
            <div
              className={`${styles.methodCard} ${method === "sms" ? styles.selected : ""}`}
              onClick={() => setMethod("sms")}
            >
              <div className={styles.methodIcon}>
                <SmsIcon selected={method === "sms"} />
              </div>
              <div className={styles.methodTitle}>SMS Code</div>
              <div className={styles.methodDesc}>Receive a code via SMS</div>
            </div>

            <div
              className={`${styles.methodCard} ${method === "call" ? styles.selected : ""}`}
              onClick={() => setMethod("call")}
            >
              <div className={styles.methodIcon}>
                <VoiceCallIcon selected={method === "call"} />
              </div>
              <div className={styles.methodTitle}>Voice Call</div>
              <div className={styles.methodDesc}>We'll read your code aloud</div>
            </div>
          </div>
        </div>

        <div className={styles.securityNote}>
          <SecurityShieldIcon style={{ flexShrink: 0 }} />
          <span>Your phone number is used for account security only. It will not appear on your Member profile or be shared with viewers.</span>
        </div>

        <button
          type="button"
          className={styles.skipBtn}
          onClick={handleSkip}
          disabled={isUpdatingPhone}
        >
          {isUpdatingPhone ? "Saving..." : "Skip"}
        </button>

        <hr className={styles.divider} />

        <div className={styles.actionRow} style={{ marginBottom: 16 }}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={handleBack}
            title={!canGoBack ? "Cannot go backward" : undefined}
            style={!canGoBack ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            disabled={isUpdatingPhone}
          >
            <ArrowLeft size={18} /> Back
          </button>
          <button
            type="submit"
            className={styles.continueBtn}
            disabled={isUpdatingPhone}
          >
            <span className={styles.desktopBtnText}>
              {isUpdatingPhone ? "Please wait..." : "Continue"}
            </span>
            <span className={styles.mobileBtnText}>
              {isUpdatingPhone ? "Please wait..." : "Verify Phone Number"}
            </span>
          </button>
        </div>
      </form>

      <div className={styles.footerLink}>
        Code not arriving? <Link to="#" className={styles.blueLink}>Check your Spam Folder</Link>
      </div>
    </div>
  );
}
